import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, LogIn, MailCheck, UserPlus } from "lucide-react";
import {
  errorMessage,
  parentForgotPassword,
  parentLogin,
  parentResetPassword,
  parentSignup,
} from "../lib/api";
import useSession from "../lib/useSession";
import { Alert, Button, Select, TextField, Field, Input } from "../ui";
import { AuthLayout } from "./layouts";

function safeNext(next) {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/portal";
}

export function PasswordInput({ id, label, value, onChange, autoComplete, hint, error }) {
  const [show, setShow] = useState(false);
  return (
    <Field label={label} htmlFor={id} hint={hint} error={error} required>
      <div className="relative">
        <Input
          id={id}
          name={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required
          className="pr-11"
          invalid={!!error}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center text-slate-500 hover:text-navy-700"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </Field>
  );
}

export function ParentLoginPage() {
  const auth = useSession("parent");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const next = safeNext(params.get("next"));

  if (auth?.access) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await parentLogin(form.email.trim(), form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Incorrect email or password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue your child's application, upload documents and see updates from the admissions office."
      footer={
        <>
          New to TIS?{" "}
          <Link to={`/admissions/signup${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="font-semibold text-navy-700 hover:underline">
            Create a parent account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <TextField
          id="email"
          label="Email address"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <PasswordInput
          id="password"
          label="Password"
          autoComplete="current-password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <div className="flex justify-end">
          <Link to="/admissions/forgot-password" className="text-sm font-semibold text-navy-700 hover:underline">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={loading} icon={LogIn} disabled={!form.email || !form.password}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

const EMPTY_SIGNUP = {
  username: "",
  relationship: "Mother",
  phone_number: "",
  email: "",
  password: "",
  confirm: "",
  occupation: "",
  organisation: "",
};

export function ParentSignupPage() {
  const auth = useSession("parent");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY_SIGNUP);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const next = safeNext(params.get("next") || "/portal/apply");

  if (auth?.access) return <Navigate to={next} replace />;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.username.trim()) errs.username = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (form.phone_number.replace(/\D/g, "").length < 10) errs.phone_number = "Please enter a valid mobile number.";
    if (form.password.length < 8) errs.password = "Use at least 8 characters.";
    else if (/^\d+$/.test(form.password)) errs.password = "Use letters as well as numbers.";
    if (form.confirm !== form.password) errs.confirm = "Passwords do not match.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const { confirm, ...payload } = form;
      await parentSignup({ ...payload, email: payload.email.trim() });
      navigate(next, { replace: true });
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) {
        const mapped = {};
        Object.entries(fieldErrors).forEach(([k, v]) => (mapped[k] = Array.isArray(v) ? v[0] : String(v)));
        setErrors(mapped);
      }
      setError(errorMessage(err, "We could not create your account."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create a parent account"
      subtitle="One account for all your children. It takes a minute, then you can start the application."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/admissions/login" className="font-semibold text-navy-700 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="grid gap-5 sm:grid-cols-[1fr_9rem]">
          <TextField id="username" label="Your full name" autoComplete="name" required value={form.username} onChange={set("username")} error={errors.username} />
          <Field label="You are the" htmlFor="relationship" required>
            <Select id="relationship" value={form.relationship} onChange={set("relationship")}>
              <option>Mother</option>
              <option>Father</option>
              <option>Guardian</option>
            </Select>
          </Field>
        </div>
        <TextField id="email" label="Email address" type="email" autoComplete="email" required value={form.email} onChange={set("email")} error={errors.email} hint="We'll send application updates here." />
        <TextField
          id="phone_number"
          label="Mobile / WhatsApp number"
          type="tel"
          autoComplete="tel"
          required
          placeholder="03xx xxxxxxx"
          value={form.phone_number}
          onChange={set("phone_number")}
          error={errors.phone_number}
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField id="occupation" label="Occupation" value={form.occupation} onChange={set("occupation")} />
          <TextField id="organisation" label="Organisation" value={form.organisation} onChange={set("organisation")} />
        </div>
        <PasswordInput id="password" label="Password" autoComplete="new-password" value={form.password} onChange={set("password")} error={errors.password} hint="At least 8 characters." />
        <PasswordInput id="confirm" label="Confirm password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} error={errors.confirm} />
        <Button type="submit" size="lg" className="w-full" loading={loading} icon={UserPlus}>
          Create account &amp; continue
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage({ brand = "admissions", onRequest = parentForgotPassword, loginPath = "/admissions/login" }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onRequest(email.trim());
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter the email you signed up with and we'll send you a link to choose a new password."
      backTo={loginPath}
      backLabel="Back to sign in"
      brand={brand}
    >
      {sent ? (
        <Alert tone="success" title="Check your inbox">
          If an account exists for <strong>{email}</strong>, we've sent a reset link. It works for 48 hours. Don't forget to check
          your spam folder.
        </Alert>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          {error && <Alert tone="error">{error}</Alert>}
          <TextField id="email" label="Email address" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <Button type="submit" size="lg" className="w-full" loading={loading} icon={MailCheck} disabled={!email}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage({ brand = "admissions", onReset = parentResetPassword, loginPath = "/admissions/login" }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Use at least 8 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await onReset(token, form.password, form.confirm);
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Choose a new password" backTo={loginPath} backLabel="Back to sign in" brand={brand}>
      {!token ? (
        <Alert tone="error">This link is incomplete. Please use the full link from the email, or request a new one.</Alert>
      ) : done ? (
        <div className="space-y-5">
          <Alert tone="success" title="Password changed">You can now sign in with your new password.</Alert>
          <Button to={loginPath} size="lg" className="w-full">
            Go to sign in
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          {error && <Alert tone="error">{error}</Alert>}
          <PasswordInput id="password" label="New password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} hint="At least 8 characters." />
          <PasswordInput id="confirm" label="Confirm new password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
          <Button type="submit" size="lg" className="w-full" loading={loading}>
            Save new password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
